import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { observer } from 'mobx-react';
import { Checkbox, message, Popconfirm, Popover, Tag, theme } from 'antd';
import moment from 'moment';

import { AlertEntity } from '@/comp/AlertsBar/Alert';
import BoxArrowDownIcon from '@/comp/common/icons/BoxArrowDownIcon';
import SeverityIcon from '@/comp/common/icons/SeverityIcon';
import SlidersHorizontalIcon from '@/comp/common/icons/SlidersHorizontalIcon';
import { EPButton } from '@/components';
import { permissions } from '@/constants';
import { useStores } from '@/hooks/use-stores';

import { AlertContainer } from './Alert.styles';
import { AlertStatus } from '@/constants/enums';

const Alert = observer(({ alert, archived }: { alert: AlertEntity; archived?: boolean }) => {
  const params = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const { alertStore, permissionStore } = useStores();

  const onSelect = (alert: AlertEntity) => () => {
    alert.isSelected = !alert.isSelected;
    if (alert.isSelected) {
      alertStore.addSelectedAlert(alert);
    } else {
      alertStore.removeSelectedAlert(alert);
    }
  };

  const onClickArchive = id => async () => {
    await alertStore.archive(id);
  };

  const onClickUnArchive = id => async () => {
    try {
      await alertStore.unArchive(id);
    } catch {
      message.error(t('Unarchive alert failed'));
    }
  };

  const onClickRuleIcon = (alert: AlertEntity) => () => {
    navigate(`/home/station/${params.stationId}/rules/${alert.alertType}/${alert.alertRuleId}/${alert.id}/view`);
  };
  return (
    <AlertContainer $isOpen={alert.status === AlertStatus.Open}>
      {!archived && <Checkbox checked={alert.isSelected} onChange={onSelect(alert)} />}
      <div className="alert">
        <div className="alert-header">
          <div className="alert-icon-severity-container">
            <div className="alert-icon-severity-label">
              <SeverityIcon severity={alert.severity} width={24} height={24} />
              <span className="alert-severity">{t(alert.severity)}</span>
              {alert.status === AlertStatus.Close && <Tag>{t('Expired')}</Tag>}
            </div>
          </div>
          <div className="alert-date">{moment(alert.openingTime).calendar()}</div>
        </div>
        {alert.title && <span className="alert-title">{alert.title}</span>}
        {alert.detailedDescription && <span className="alert-description">{alert.detailedDescription}</span>}
        <div className="alert-footer">
          {permissionStore.isAuth(permissions.UPDATE_ALERT_RECORD) &&
            (alert.acknowledged === false && !alert.inMaintenance ? (
              <Popover placement="bottom" content={<span>{t('Archive Alert')}</span>}>
                <EPButton
                  type="link"
                  size="medium"
                  icon={<BoxArrowDownIcon color={token.colorPrimary} />}
                  onClick={onClickArchive(alert.id)}
                />
              </Popover>
            ) : (
              alert.acknowledged === true &&
              !alert.inMaintenance && (
                <Popover placement="bottom" content={<span>{t('Unarchive Alert')}</span>}>
                  <Popconfirm
                    placement="bottom"
                    title={t('Are you sure you want to unarchive this alert record?')}
                    onConfirm={onClickUnArchive(alert.id)}
                    okText={t('Yes')}
                    cancelText={t('cancel')}
                    icon={null}
                  >
                    <EPButton type="link" size="medium" icon={<BoxArrowDownIcon />} />
                  </Popconfirm>
                </Popover>
              )
            ))}
          <Popover placement="bottom" content={<span>{t(`View Alert record's rule (logic)`)}</span>}>
            <EPButton
              type="link"
              size="medium"
              icon={<SlidersHorizontalIcon width={16} height={16} color={token.colorPrimary} />}
              onClick={onClickRuleIcon(alert)}
            />
          </Popover>
        </div>
      </div>
    </AlertContainer>
  );
});

export default Alert;
